// PendingEditPage — single-form edit of a pending booking. Mirrors legacy
// `get_pending_edit.php` field order (Customer, Vehicle, Booking Details,
// Collection Address (optional toggle), New Note (optional)).
//
// Form behaviour:
//   - Populates from GET /pending/:enc_id and snapshots the initial state
//     so the dirty flag does not trip on the seed render.
//   - useBlocker on cross-route navigation when dirty.
//   - Save: PUT /pending/:enc_id with a partial patch (only changed fields).
//     If the optional new-note textarea has content, also POSTs to
//     /pending/:enc_id/notes after the save succeeds.
//   - Terminal-status banner: when `status` is `booked` or `cancelled`, the
//     form fields are disabled and a banner explains why. Server enforces
//     the same gate; this is purely UX.

import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useBlocker, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronLeft } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Card } from '../../components/ui/Card/Card';
import { Button } from '../../components/ui/Button/Button';
import { Input } from '../../components/form/Input';
import { Textarea } from '../../components/form/Textarea';
import { Select } from '../../components/form/Select';
import { Checkbox } from '../../components/form/Checkbox';
import { Spinner } from '../../components/ui/Spinner/Spinner';
import { ConfirmModal } from '../../components/ui/ConfirmModal/ConfirmModal';
import { useToast } from '../../hooks/useToast';
import {
  useAddPendingNote,
  usePendingDetail,
  useUpdatePending,
} from './hooks/usePending';
import {
  useProducts,
  useWorkSources,
} from '../bookings/hooks/useReferenceData';
import type { UpdatePendingPayload } from '../../types/api';
import styles from './PendingEditPage.module.css';

interface FormState {
  name: string;
  email: string;
  phone: string;
  phone2: string;
  postcode: string;
  address: string;
  car_make_model: string;
  car_registration: string;
  price: string;
  product_id: string;
  source: string;
  hasCollection: boolean;
  collection_address: string;
  collection_postcode: string;
  newNote: string;
}

const EMPTY: FormState = {
  name: '',
  email: '',
  phone: '',
  phone2: '',
  postcode: '',
  address: '',
  car_make_model: '',
  car_registration: '',
  price: '',
  product_id: '',
  source: '',
  hasCollection: false,
  collection_address: '',
  collection_postcode: '',
  newNote: '',
};

function snapshotKey(state: FormState): string {
  return JSON.stringify(state);
}

export function PendingEditPage(): JSX.Element {
  const { id: encId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();

  const detail = usePendingDetail(encId);
  const update = useUpdatePending(encId);
  const addNote = useAddPendingNote(encId);
  const products = useProducts();
  const sources = useWorkSources();

  const [state, setState] = useState<FormState>(EMPTY);
  const baselineRef = useRef<string | null>(null);
  const [baselineCaptured, setBaselineCaptured] = useState(false);

  // Hydrate the form once the detail is in. The dirty baseline is captured
  // on the first hydration so the initial seed does not flag dirty.
  useEffect(() => {
    if (baselineCaptured || !detail.data) return;
    const p = detail.data.pending_booking;
    const populated: FormState = {
      name: p.name,
      email: p.email ?? '',
      phone: p.phone,
      phone2: p.phone2 ?? '',
      postcode: p.postcode,
      address: p.address,
      car_make_model: p.car_make_model,
      car_registration: p.car_registration,
      price: p.price === null ? '' : String(p.price),
      product_id: p.product_id === null ? '' : String(p.product_id),
      source: p.source_id === null ? '' : String(p.source_id),
      hasCollection: p.collection_address !== null,
      collection_address: p.collection_address ?? '',
      collection_postcode: p.collection_postcode ?? '',
      newNote: '',
    };
    setState(populated);
    baselineRef.current = snapshotKey(populated);
    setBaselineCaptured(true);
  }, [baselineCaptured, detail.data]);

  const dirty =
    baselineCaptured &&
    baselineRef.current !== null &&
    baselineRef.current !== snapshotKey(state);

  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    dirty && currentLocation.pathname !== nextLocation.pathname,
  );

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setState((prev) => ({ ...prev, [key]: value }));
  }

  const productOptions = useMemo(
    () => [
      { value: '', label: '-- Select --' },
      ...(products.data ?? []).map((p) => ({ value: String(p.id), label: p.name })),
    ],
    [products.data],
  );

  const sourceOptions = useMemo(
    () => [
      { value: '', label: '-- Select --' },
      ...(sources.data ?? []).map((s) => ({ value: String(s.id), label: s.name })),
    ],
    [sources.data],
  );

  if (detail.isLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>
          <Spinner size="md" />
          <span>Loading pending booking…</span>
        </div>
      </div>
    );
  }

  if (detail.isError || !detail.data) {
    return (
      <div className={styles.page}>
        <PageHeader title="Edit pending booking" subtitle="Could not load this booking." />
        <Button variant="secondary" onClick={() => navigate('/collections/pending')}>
          Back
        </Button>
      </div>
    );
  }

  const pending = detail.data.pending_booking;
  const terminal = pending.status === 'booked' || pending.status === 'cancelled';

  function buildPatch(): UpdatePendingPayload {
    // Only include fields that changed from the baseline-captured row.
    const p = pending;
    const patch: UpdatePendingPayload = {};
    if (state.name !== p.name) patch.name = state.name;
    if (state.email !== (p.email ?? '')) patch.email = state.email === '' ? null : state.email;
    if (state.phone !== p.phone) patch.phone = state.phone;
    if (state.phone2 !== (p.phone2 ?? '')) patch.phone2 = state.phone2 === '' ? null : state.phone2;
    if (state.postcode !== p.postcode) patch.postcode = state.postcode;
    if (state.address !== p.address) patch.address = state.address;
    if (state.car_make_model !== p.car_make_model) patch.car_make_model = state.car_make_model;
    if (state.car_registration !== p.car_registration) patch.car_registration = state.car_registration;
    const priceNum = state.price === '' ? null : Number(state.price);
    if (priceNum !== p.price) patch.price = priceNum;
    const productNum = state.product_id === '' ? null : Number(state.product_id);
    if (productNum !== p.product_id) patch.product_id = productNum;
    const sourceNum = state.source === '' ? null : Number(state.source);
    if (sourceNum !== p.source_id) patch.source = sourceNum;

    const wantCollection = state.hasCollection;
    const newCollAddr = wantCollection ? state.collection_address : '';
    const newCollPc = wantCollection ? state.collection_postcode : '';
    if (newCollAddr !== (p.collection_address ?? '')) {
      patch.collection_address = newCollAddr === '' ? null : newCollAddr;
    }
    if (newCollPc !== (p.collection_postcode ?? '')) {
      patch.collection_postcode = newCollPc === '' ? null : newCollPc;
    }
    return patch;
  }

  async function handleSave() {
    if (!encId || terminal) return;
    const patch = buildPatch();
    const noteText = state.newNote.trim();

    if (Object.keys(patch).length === 0 && noteText.length === 0) {
      toast.info('Nothing to save.');
      return;
    }

    try {
      if (Object.keys(patch).length > 0) {
        await update.mutateAsync(patch);
      }
      if (noteText.length > 0) {
        await addNote.mutateAsync({ note: noteText });
      }
      toast.success('Pending booking updated.');
      // Reset baseline so the post-save form isn't immediately marked dirty.
      baselineRef.current = snapshotKey({ ...state, newNote: '' });
      setState((prev) => ({ ...prev, newNote: '' }));
      await queryClient.invalidateQueries();
      navigate(`/collections/pending/${encodeURIComponent(encId)}`);
    } catch (err) {
      const msg = axios.isAxiosError(err)
        ? (err.response?.data as { message?: string } | undefined)?.message ??
          'Could not update pending booking.'
        : 'Could not update pending booking.';
      toast.error(msg);
    }
  }

  const submitting = update.isPending || addNote.isPending;
  const disabled = terminal || submitting;

  return (
    <div className={styles.page}>
      <button
        type="button"
        onClick={() => navigate(`/collections/pending/${encodeURIComponent(encId ?? '')}`)}
        className={styles.back}
      >
        <ChevronLeft size={14} aria-hidden /> Back to pending
      </button>

      <PageHeader
        title="Edit Pending Booking"
        subtitle={pending.reference}
      />

      {terminal ? (
        <div className={styles.terminalBanner} role="alert">
          <strong>This pending booking is {pending.status} and cannot be edited.</strong>
          <span>Open the detail page to view its history.</span>
        </div>
      ) : null}

      <Card>
        <h3 className={styles.sectionTitle}>Customer information</h3>
        <div className={styles.grid}>
          <Input
            label={<>Name <span aria-hidden>*</span></>}
            required
            value={state.name}
            onChange={(e) => set('name', e.target.value)}
            disabled={disabled}
          />
          <Input
            label="Email"
            variant="email"
            value={state.email}
            onChange={(e) => set('email', e.target.value)}
            disabled={disabled}
          />
          <Input
            label={<>Phone <span aria-hidden>*</span></>}
            required
            value={state.phone}
            onChange={(e) => set('phone', e.target.value)}
            disabled={disabled}
          />
          <Input
            label="Secondary phone"
            value={state.phone2}
            onChange={(e) => set('phone2', e.target.value)}
            disabled={disabled}
          />
          <Input
            label={<>Postcode <span aria-hidden>*</span></>}
            required
            value={state.postcode}
            onChange={(e) => set('postcode', e.target.value)}
            disabled={disabled}
          />
          <div className={styles.fullRow}>
            <Textarea
              label={<>Address <span aria-hidden>*</span></>}
              required
              value={state.address}
              onChange={(e) => set('address', e.target.value)}
              rows={2}
              autoResize
              disabled={disabled}
            />
          </div>
        </div>
      </Card>

      <Card>
        <h3 className={styles.sectionTitle}>Vehicle information</h3>
        <div className={styles.grid}>
          <Input
            label={<>Make &amp; model <span aria-hidden>*</span></>}
            required
            value={state.car_make_model}
            onChange={(e) => set('car_make_model', e.target.value)}
            disabled={disabled}
          />
          <Input
            label={<>Registration <span aria-hidden>*</span></>}
            required
            value={state.car_registration}
            onChange={(e) => set('car_registration', e.target.value)}
            disabled={disabled}
          />
          <Input
            label="Price (£)"
            variant="number"
            step="0.01"
            min="0"
            value={state.price}
            onChange={(e) => set('price', e.target.value)}
            disabled={disabled}
          />
        </div>
      </Card>

      <Card>
        <h3 className={styles.sectionTitle}>Booking details</h3>
        <div className={styles.grid}>
          <Select
            label="Product"
            options={productOptions}
            value={state.product_id}
            onChange={(e) => set('product_id', e.target.value)}
            disabled={disabled}
          />
          <Select
            label="Source"
            options={sourceOptions}
            value={state.source}
            onChange={(e) => set('source', e.target.value)}
            disabled={disabled}
          />
        </div>
      </Card>

      <Card>
        <h3 className={styles.sectionTitle}>Collection address</h3>
        <Checkbox
          label="Collection address is different from the customer's address"
          checked={state.hasCollection}
          onChange={(e) => set('hasCollection', e.target.checked)}
          disabled={disabled}
        />
        {state.hasCollection ? (
          <div className={styles.grid}>
            <Input
              label="Collection postcode"
              value={state.collection_postcode}
              onChange={(e) => set('collection_postcode', e.target.value)}
              disabled={disabled}
            />
            <div className={styles.fullRow}>
              <Textarea
                label="Collection address"
                value={state.collection_address}
                onChange={(e) => set('collection_address', e.target.value)}
                rows={2}
                autoResize
                disabled={disabled}
              />
            </div>
          </div>
        ) : null}
      </Card>

      <Card>
        <h3 className={styles.sectionTitle}>Add a note (optional)</h3>
        <Textarea
          label="New note"
          value={state.newNote}
          onChange={(e) => set('newNote', e.target.value)}
          rows={3}
          autoResize
          placeholder="Add a note about this update — appended to the booking history when you save."
          disabled={disabled}
        />
      </Card>

      <div className={styles.actions}>
        <Button
          variant="secondary"
          onClick={() => navigate(`/collections/pending/${encodeURIComponent(encId ?? '')}`)}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={() => void handleSave()}
          loading={submitting}
          disabled={disabled || !dirty}
        >
          Save Changes
        </Button>
      </div>

      <ConfirmModal
        open={blocker.state === 'blocked'}
        title="Discard unsaved changes?"
        message="You have unsaved changes. Leaving the page will discard them."
        confirmLabel="Discard"
        variant="danger"
        onConfirm={() => blocker.proceed?.()}
        onCancel={() => blocker.reset?.()}
      />
    </div>
  );
}
